import {
  type AcceptDecisionSuggestionInput,
  type AiSuggestionView,
  type AnswerDecisionInput,
  type ChangeDecisionDecidersInput,
  type ClearDecisionAnswerInput,
  type CreateDecisionShareLinkInput,
  type CreateDecisionSheetInput,
  type DecisionAnswerHistoryEntry,
  type DecisionQuestionView,
  type DecisionShareLinkView,
  type DecisionSheetFile,
  type DecisionSheetSummary,
  type DecisionSheetView,
  type DraftDecisionSheetInput,
  type ExtractDecisionSheetInput,
  type ExtractDecisionSheetResult,
  type PublicAnswerDecisionInput,
  type PublicClearDecisionAnswerInput,
  type PublicDecisionSheetView,
  type RecordDecisionExportInput,
  type RecordDecisionRaidInput,
  type RejectDecisionSuggestionInput,
  type ReorderDecisionQuestionsInput,
  type TaskDecisionSheetView,
  type UpdateDecisionSheetInput,
  type UpsertDecisionGroupInput,
  type UpsertDecisionQuestionInput,
} from '@apm/contracts';
import { apiFetch } from '../../lib/http';

/**
 * Sổ chốt phương án — APM-DSHEET-c/d. Route theo hợp đồng ở
 * docs/plan-decision-sheet.md §4–§5 (module API `decision-sheets`).
 *
 * Body luôn là **object** — `apiFetch` tự stringify (bẫy "body serialize hai
 * lần" ở CLAUDE.md).
 */
export const decisionSheetApi = {
  list: (projectId: string) =>
    apiFetch<DecisionSheetSummary[]>(`/projects/${projectId}/decision-sheets`),

  create: (projectId: string, body: CreateDecisionSheetInput) =>
    apiFetch<{ id: string }>(`/projects/${projectId}/decision-sheets`, {
      method: 'POST',
      body,
    }),

  /** `asOf` (ISO) ⇒ sổ dựng lại từ event tại mốc ấy (APM-DSHEET-b, luật #7). */
  get: (sheetId: string, asOf?: string) =>
    apiFetch<DecisionSheetView>(
      `/decision-sheets/${sheetId}${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`,
    ),

  update: (sheetId: string, body: UpdateDecisionSheetInput) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}`, { method: 'PATCH', body }),

  remove: (sheetId: string) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}`, { method: 'DELETE' }),

  upsertGroup: (sheetId: string, body: UpsertDecisionGroupInput) =>
    apiFetch<{ id: string }>(`/decision-sheets/${sheetId}/groups`, { method: 'POST', body }),

  removeGroup: (sheetId: string, groupId: string) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/groups/${groupId}`, { method: 'DELETE' }),

  upsertQuestion: (sheetId: string, body: UpsertDecisionQuestionInput) =>
    apiFetch<{ id: string }>(`/decision-sheets/${sheetId}/questions`, { method: 'POST', body }),

  removeQuestion: (sheetId: string, questionId: string) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/questions/${questionId}`, {
      method: 'DELETE',
    }),

  reorderQuestions: (sheetId: string, body: ReorderDecisionQuestionsInput) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/questions/reorder`, {
      method: 'POST',
      body,
    }),

  changeDeciders: (sheetId: string, body: ChangeDecisionDecidersInput) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/deciders`, { method: 'POST', body }),

  transition: (sheetId: string, action: 'open' | 'close' | 'reopen') =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/${action}`, { method: 'POST' }),

  answer: (sheetId: string, questionId: string, body: AnswerDecisionInput) =>
    apiFetch<DecisionQuestionView>(`/decision-sheets/${sheetId}/questions/${questionId}/answer`, {
      method: 'PUT',
      body,
    }),

  clearAnswer: (sheetId: string, questionId: string, body: ClearDecisionAnswerInput) =>
    apiFetch<DecisionQuestionView>(
      `/decision-sheets/${sheetId}/questions/${questionId}/clear-answer`,
      { method: 'POST', body },
    ),

  history: (sheetId: string, questionId: string) =>
    apiFetch<{ entries: DecisionAnswerHistoryEntry[] }>(
      `/decision-sheets/${sheetId}/questions/${questionId}/history`,
    ),

  /** JSON v1 kèm `answers` — "Tải JSON" (APM-DSHEET-d). */
  exportFile: (sheetId: string) =>
    apiFetch<DecisionSheetFile>(`/decision-sheets/${sheetId}/export.json`),

  /** APM-DSHEET-e — after the web created the document / added a version. */
  recordExport: (sheetId: string, body: RecordDecisionExportInput) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/exported`, { method: 'POST', body }),

  /** APM-DSHEET-f — after the web created the RAID `DECISION` items. */
  recordRaid: (sheetId: string, body: RecordDecisionRaidInput) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/raid-recorded`, { method: 'POST', body }),

  /**
   * AI soạn sổ từ 1–5 tài liệu dự án (APM-DSHEET-g) ⇒ gợi ý chờ duyệt. Gọi
   * trong `useTrackedAiRun` để có `runId` và bảng bước bên phải.
   */
  aiDraft: (projectId: string, body: DraftDecisionSheetInput) =>
    apiFetch<AiSuggestionView>(`/projects/${projectId}/decision-sheets/ai/draft`, {
      method: 'POST',
      body,
    }),

  /** Văn bản / HTML dán vào (APM-DSHEET-h). JSON v1 thì server trả thẳng, không gọi AI. */
  aiExtract: (projectId: string, body: ExtractDecisionSheetInput) =>
    apiFetch<ExtractDecisionSheetResult>(`/projects/${projectId}/decision-sheets/ai/extract`, {
      method: 'POST',
      body,
    }),

  /** "Tạo sổ" từ gợi ý — `file` là bản đã sửa; vắng ⇒ server dùng nội dung gốc. */
  acceptSuggestion: (suggestionId: string, body: AcceptDecisionSuggestionInput) =>
    apiFetch<{ id: string }>(`/decision-sheets/ai/suggestions/${suggestionId}/accept`, {
      method: 'POST',
      body,
    }),

  rejectSuggestion: (suggestionId: string, body: RejectDecisionSuggestionInput = {}) =>
    apiFetch<{ ok: true }>(`/decision-sheets/ai/suggestions/${suggestionId}/reject`, {
      method: 'POST',
      body,
    }),

  // ── Đợt 2 (APM-DSX-b) — link công khai, plan §11.3 ──

  shares: (sheetId: string) =>
    apiFetch<DecisionShareLinkView[]>(`/decision-sheets/${sheetId}/shares`),

  /** Phản hồi mang `url` đầy đủ — chép ngay được, khỏi gọi thêm `shareUrl`. */
  createShare: (sheetId: string, body: CreateDecisionShareLinkInput) =>
    apiFetch<DecisionShareLinkView & { url: string }>(`/decision-sheets/${sheetId}/shares`, {
      method: 'POST',
      body,
    }),

  /** Token chỉ lưu dạng băm + niêm phong; chép lại link là xin server mở niêm phong. */
  shareUrl: (sheetId: string, shareId: string) =>
    apiFetch<{ url: string }>(`/decision-sheets/${sheetId}/shares/${shareId}/url`),

  revokeShare: (sheetId: string, shareId: string) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/shares/${shareId}`, { method: 'DELETE' }),

  // ── Đợt 2 (APM-DSX-d) — giao việc trả lời, plan §11.2 ──

  /** Nối task vào sổ; `addedDecider` = người được giao vừa thành người chốt. */
  linkTask: (sheetId: string, taskId: string) =>
    apiFetch<{ ok: true; addedDecider: boolean }>(`/decision-sheets/${sheetId}/tasks`, {
      method: 'POST',
      body: { taskId },
    }),

  unlinkTask: (sheetId: string, taskId: string) =>
    apiFetch<{ ok: true }>(`/decision-sheets/${sheetId}/tasks/${taskId}`, { method: 'DELETE' }),

  /** Khối "Sổ chốt phương án" trong chi tiết task. */
  taskSheets: (projectId: string, taskId: string) =>
    apiFetch<TaskDecisionSheetView[]>(`/projects/${projectId}/tasks/${taskId}/decision-sheets`),
};

/**
 * Trang công khai `/d/$token` — APM-DSX-b. Cùng một đường cho người chưa và
 * đã đăng nhập: route là `@Public()` và **đọc user nếu có**, còn `apiFetch` tự
 * gắn access token khi có phiên ⇒ đã đăng nhập thì server lấy tên/email từ tài
 * khoản (`verified: true`) và bỏ qua `respondent` gửi lên.
 */
export const publicDecisionApi = {
  get: (token: string) =>
    apiFetch<PublicDecisionSheetView>(`/public/decision-shares/${encodeURIComponent(token)}`),

  answer: (token: string, questionId: string, body: PublicAnswerDecisionInput) =>
    apiFetch<DecisionQuestionView>(
      `/public/decision-shares/${encodeURIComponent(token)}/questions/${questionId}/answer`,
      { method: 'PUT', body },
    ),

  clearAnswer: (token: string, questionId: string, body: PublicClearDecisionAnswerInput) =>
    apiFetch<DecisionQuestionView>(
      `/public/decision-shares/${encodeURIComponent(token)}/questions/${questionId}/clear-answer`,
      { method: 'POST', body },
    ),
};

/**
 * Dưới `['projects', id]` nên mọi chỗ làm mới dự án cũng làm mới danh sách sổ.
 * Sổ đọc theo id riêng (`['decision-sheet', id, asOf]`) vì route của nó không
 * lồng dưới dự án.
 */
export const decisionSheetKeys = {
  list: (projectId: string) => ['projects', projectId, 'decision-sheets'] as const,
  all: ['decision-sheet'] as const,
  detail: (sheetId: string, asOf?: string) => ['decision-sheet', sheetId, asOf ?? null] as const,
  history: (sheetId: string, questionId: string) =>
    ['decision-sheet', sheetId, 'history', questionId] as const,
  /** Dưới `['decision-sheet', id]` ⇒ mọi chỗ làm mới sổ cũng làm mới danh sách link. */
  shares: (sheetId: string) => ['decision-sheet', sheetId, 'shares'] as const,
  /** Dưới `['projects', id]` như danh sách sổ. */
  taskSheets: (projectId: string, taskId: string) =>
    ['projects', projectId, 'tasks', taskId, 'decision-sheets'] as const,
  /** Trang công khai — không lồng dưới sổ: người xem không biết id sổ. */
  public: (token: string) => ['public-decision-share', token] as const,
};
